import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { chatFrom, planProgress } from "@foreman/app-core";
import type { OfficeAgent, OfficeMessage } from "@foreman/protocol";
import type { Nav } from "../nav";
import { Buddy } from "../team-world/Buddy";
import { TeamTour, TOUR } from "../team-world/Tour";
import { AgentSheet, ChatSheet, HandoverSheet, Sheet } from "../team-world/Sheets";
import { goalTitle, isTyping, planOf, stillPending, withPending, withPlan, type Pending } from "../team-world/chat";
import { MOOD, PILL, boardRow, deskColors, deskOf, leadMood, lookOf, type Desk } from "../team-world/look";
import { Spinner, useApp, useConnection, useHubSummaries } from "../ui";
import "../team-world/world.css";

/** How much slower than the design file the room runs (`--tw-k` in world.css). Read from there so the two cannot drift apart. */
const speed = (): number => {
  if (typeof document === "undefined") return 1;
  return Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--tw-k")) || 1;
};
const POP_AFTER_MS = 560;
const LEAVE_MS = 600;
const FLIGHT_MS = 1150;
const SAY_FRESH_MS = 5 * 60_000;
const SUGGESTIONS = ["Look through the open TODOs and fix the easiest three.", "Find why the tests are slow and speed them up."];

/** A handful of lilac motes that drift up once, the first time the room opens. Fixed so the picture is the same every time. */
const SPARKS = [
  { x: 8, s: 5, d: 700, dx: 14 }, { x: 19, s: 4, d: 1200, dx: -10 }, { x: 31, s: 6, d: 900, dx: 12 }, { x: 44, s: 4, d: 1500, dx: -14 },
  { x: 56, s: 5, d: 800, dx: 10 }, { x: 67, s: 4, d: 1300, dx: -12 }, { x: 78, s: 6, d: 1000, dx: 16 }, { x: 89, s: 5, d: 1600, dx: -8 },
];

/** Leaving: a veil of the bloom colour outlives this screen and fades over Home, which settles in underneath (see world.css). */
function playVeil(k: number): void {
  const veil = document.createElement("div");
  veil.className = "tw-veil";
  veil.setAttribute("aria-hidden", "true");
  document.body.appendChild(veil);
  document.documentElement.dataset.twReturn = "1";
  const done = () => {
    veil.remove();
    delete document.documentElement.dataset.twReturn;
  };
  veil.addEventListener("animationend", done, { once: true });
  setTimeout(done, 760 * k + 900); // a stuck animation must never leave a veil over the app
}

const reducedMotion = () => typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

interface Flight {
  key: string;
  from: string;
  to: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  at: 0 | 1;
}

/**
 * The team's playground: a room where the coordinator keeps the plan on a whiteboard and each agent works at its own desk.
 * Tap an agent for what it is doing and has done, tap the coordinator or the dock to talk to it. When one agent hands work to
 * another, an envelope flies between their desks.
 */
export function TeamsScreen({ nav }: { nav: Nav }) {
  const { hub, toast } = useApp();
  const summaries = useHubSummaries(hub);
  const [pick, setPick] = useState<string | undefined>();
  const id = pick ?? summaries.find((s) => s.state === "ready")?.computer.id ?? summaries[0]?.computer.id ?? "";
  const { conn, snap, computer } = useConnection(id);
  const ready = snap.state === "ready";
  const office = snap.office ?? null;
  const live = snap.officeActivity ?? {};
  const messages = snap.officeMessages ?? [];

  // ---- coming in and going out
  const reduced = useMemo(reducedMotion, []);
  const k = useMemo(speed, []);
  const [open, setOpen] = useState(reduced);
  const [pop, setPop] = useState(reduced);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (reduced) return;
    const a = requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
    const b = setTimeout(() => setPop(true), POP_AFTER_MS * k);
    return () => {
      cancelAnimationFrame(a);
      clearTimeout(b);
    };
  }, [reduced, k]);
  const leave = useCallback(() => {
    setPop(false);
    setClosing(true);
    setTimeout(
      () => {
        if (!reduced) playVeil(k);
        nav.goTab({ name: "home" });
      },
      reduced ? 0 : LEAVE_MS * k,
    );
  }, [nav, reduced, k]);
  const [pageHidden, setPageHidden] = useState(false);
  useEffect(() => {
    const on = () => setPageHidden(document.hidden);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  // first time in? (read before it is marked visited) decides the NEW chip, the sparkles and the welcome
  const [first] = useState(() => {
    try {
      return localStorage.getItem("foreman.teams.visited") !== "1";
    } catch {
      return false;
    }
  });
  const [sparks, setSparks] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem("foreman.teams.visited", "1");
    } catch {
      /* private mode */
    }
  }, []);
  useEffect(() => {
    if (!pop || !first || reduced) return;
    setSparks(true);
    const t = setTimeout(() => setSparks(false), 4200 * k);
    return () => clearTimeout(t);
  }, [pop, first, reduced, k]);

  // ---- data: ask now, keep fresh, seed the chat and the feed
  useEffect(() => {
    if (!ready || !conn) return;
    conn.refreshOffice();
    void conn.officeLoadMessages();
    void conn.officeHistory({ limit: 40 });
    const t = setInterval(() => conn.refreshOffice(), 5000);
    return () => clearInterval(t);
  }, [ready, conn]);

  const agents = office?.agents ?? [];
  const tasks = office?.tasks ?? [];
  const lead = agents.find((a) => a.isGod);
  const leadId = lead?.id ?? "god";
  const onFloor = useMemo(() => agents.filter((a) => !a.isGod && !a.archived), [agents]);
  const earlier = useMemo(() => agents.filter((a) => !a.isGod && a.archived), [agents]);
  const byId = useCallback((aid: string) => agents.find((a) => a.id === (aid === "god" ? leadId : aid)), [agents, leadId]);
  const nameOf = useCallback((aid: string) => byId(aid)?.name ?? aid, [byId]);
  const lookFor = useCallback((aid: string) => (byId(aid) ? lookOf(byId(aid)!) : { color: "oklch(0.7 0.02 282)", shape: "50%" }), [byId]);

  // ---- hand-overs in flight
  const rootRef = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLButtonElement>(null);
  const artRefs = useRef(new Map<string, HTMLDivElement>());
  const [flight, setFlight] = useState<Flight | null>(null);
  const queue = useRef<{ key: string; from: string; to: string }[]>([]);
  const running = useRef(false);
  const seenLive = useRef<string | null>(null);
  const [handing, setHanding] = useState<string | null>(null);

  const centre = useCallback((aid: string): { x: number; y: number } | null => {
    const root = rootRef.current?.getBoundingClientRect();
    if (!root) return null;
    if (aid === leadId) {
      const r = leadRef.current?.getBoundingClientRect();
      return r ? { x: r.left - root.left + r.width / 2, y: r.top - root.top + 36 } : null;
    }
    const r = artRefs.current.get(aid)?.getBoundingClientRect();
    return r ? { x: r.left - root.left + 80, y: r.top - root.top + 48 } : null;
  }, [leadId]);

  const pump = useCallback(() => {
    if (running.current) return;
    const next = queue.current.shift();
    if (!next) return;
    const a = centre(next.from);
    const b = centre(next.to);
    if (!a || !b) return void pump(); // one end is off the floor: skip the picture, the chat still has it
    running.current = true;
    setHanding(next.from);
    setFlight({ key: next.key, from: next.from, to: next.to, x1: a.x, y1: a.y, x2: b.x, y2: b.y, at: 0 });
    setTimeout(() => setFlight((f) => (f && f.key === next.key ? { ...f, at: 1 } : f)), 60);
    setTimeout(
      () => {
        setFlight(null);
        setHanding(null);
        running.current = false;
        pump();
      },
      reduced ? 300 : FLIGHT_MS * k,
    );
  }, [centre, reduced, k]);

  useEffect(() => {
    const m = snap.officeLive;
    if (!m || m.id === seenLive.current) return;
    seenLive.current = m.id;
    if (m.from === "human" || m.to === "human") return;
    const from = m.from === "god" ? leadId : m.from;
    const to = m.to === "god" ? leadId : m.to;
    if (from === to) return;
    queue.current.push({ key: m.id, from, to });
    pump();
  }, [snap.officeLive, leadId, pump]);

  // ---- what each desk shows
  const desks = useMemo(() => {
    const m = new Map<string, Desk>();
    for (const a of agents) m.set(a.id, deskOf(a, { latest: live[a.id]?.[0], tasks, agents, handing: handing === a.id }));
    return m;
  }, [agents, live, tasks, handing]);
  const deskFor = (a: OfficeAgent) => desks.get(a.id)!;
  const floorDesks = onFloor.map(deskFor);
  const mood = leadMood(tasks, floorDesks);

  // ---- the whiteboard
  const goal = useMemo(() => [...messages].filter((m) => m.from === "human").sort((a, b) => b.ts - a.ts)[0], [messages]);
  const goalText = goal ? goal.body.split("\n\n(Sent from the human")[0]!.trim().split("\n")[0]! : "";
  const progress = planProgress(tasks);
  const boardTitle = tasks.length === 0 ? "No mission yet" : goalText ? goalText.slice(0, 48) : "Plan";
  const boardProg = tasks.length === 0 ? "" : `${progress.done}/${progress.total}`;
  const BOARD_ROWS = 4;

  // ---- states that are not "a team at work"
  const status: "none" | "old" | "wait" | "off" | "team" =
    summaries.length === 0 ? "none" : snap.officeUnsupported ? "old" : !office ? "wait" : !office.available ? "off" : "team";

  // ---- the coordinator's bubble
  const chat = useMemo(() => chatFrom(messages, leadId), [messages, leadId]);
  const lastDot = [...chat].reverse().find((c) => c.kind === "dot");
  const [seenDot, setSeenDot] = useState<string | null>(null);
  const attn = onFloor.map((a) => ({ a, d: deskFor(a) })).find((x) => x.d.state === "wait" || x.d.state === "fail" || x.d.state === "stopped");
  const welcome = status === "team" && messages.length === 0 && tasks.length === 0;
  const bubble: { text: string; label?: string; attn?: boolean; act: "agent" | "chat"; agentId?: string } | null = attn
    ? { text: attn.d.state === "wait" ? `${attn.a.name} needs you.` : attn.d.state === "stopped" ? `${attn.a.name} was stopped.` : `${attn.a.name} hit an error.`, label: "Review", attn: true, act: "agent", agentId: attn.a.id }
    : lastDot && lastDot.id !== seenDot && Date.now() - lastDot.ts < SAY_FRESH_MS
      ? { text: lastDot.kind === "dot" ? lastDot.text : "", act: "chat" }
      : welcome
        ? { text: `Hi! I'm ${lead?.name ?? "Dot"}. Tap me and tell me what to build.`, label: "Start", act: "chat" }
        : null;

  // ---- sheets
  const [sheet, setSheet] = useState<{ kind: "agent"; id: string } | { kind: "chat" } | { kind: "hand"; m: OfficeMessage } | null>(null);
  const closeSheet = useCallback(() => setSheet(null), []);
  const openChat = () => {
    if (status !== "team") return; // no team running: nothing to talk to (the card says why)
    setSheet({ kind: "chat" });
    if (lastDot) setSeenDot(lastDot.id);
  };
  // what you just sent shows at once; the engine's echo of it replaces it a moment later
  const [pending, setPending] = useState<Pending[]>([]);
  useEffect(() => {
    setPending((p) => (p.length ? stillPending(chat, p, Date.now()) : p));
  }, [chat]);
  const items = useMemo(() => withPending(chat, pending), [chat, pending]);
  const entries = useMemo(() => withPlan(items, tasks.length > 0), [items, tasks.length]);
  const planCard = useMemo(() => (tasks.length > 0 ? { title: goalTitle(items), steps: planOf(tasks, agents) } : null), [tasks, agents, items]);
  const leadWorking = !!lead && desks.get(lead.id)?.state === "work";
  const typing = isTyping(items, Date.now(), leadWorking);
  const [prefill, setPrefill] = useState<{ text: string; n: number } | null>(null);
  const send = useCallback(
    async (text: string) => {
      const p: Pending = { id: `p${Date.now()}`, text, ts: Date.now() };
      setPending((x) => [...x, p]);
      const r = await conn?.officeSend(text);
      if (!r?.ok) {
        setPending((x) => x.filter((y) => y.id !== p.id));
        toast(r?.message ?? "Couldn't send that");
      }
      return !!r?.ok;
    },
    [conn, toast],
  );
  const done = tasks.length > 0 && progress.done === progress.total;
  const summary = done ? { done: progress.done, total: progress.total, files: [...new Set(Object.values(live).flat().filter((e) => e.kind === "edit" && e.file).map((e) => e.file!))] } : null;

  // ---- starting and stopping the team (the computer runs the engine; agents then work without asking, so starting is a choice)
  const eng = office?.engine;
  const [engineBusy, setEngineBusy] = useState(false);
  const [stopArmed, setStopArmed] = useState(false);
  useEffect(() => {
    if (!stopArmed) return;
    const t = setTimeout(() => setStopArmed(false), 4000);
    return () => clearTimeout(t);
  }, [stopArmed]);
  const runEngine = async (action: "start" | "stop") => {
    if (!conn || engineBusy) return;
    setEngineBusy(true);
    const r = await conn.officeEngine(action);
    setEngineBusy(false);
    if (!r.ok) toast(r.message ?? (action === "start" ? "Couldn't start the team" : "Couldn't stop the team"));
    conn.refreshOffice();
  };

  // the first-visit tour (replayed from the ? button): remembered per browser, never in the way of a sheet
  const [tour, setTour] = useState<number | null>(null);
  const tourSeen = () => {
    try {
      return localStorage.getItem("foreman.teams.tour") === "1";
    } catch {
      return false;
    }
  };
  const endTour = () => {
    setTour(null);
    try {
      localStorage.setItem("foreman.teams.tour", "1");
    } catch {
      /* private mode */
    }
  };
  useEffect(() => {
    if (!open || closing || tourSeen()) return;
    const t = window.setTimeout(() => setTour((v) => v ?? 0), 3200 * 1.6);
    return () => window.clearTimeout(t);
  }, [open, closing]);

  const sel = sheet?.kind === "agent" ? byId(sheet.id) : undefined;
  const cls = ["tw", open && !closing ? "open" : "", pop ? "pop" : "", closing ? "closing" : "", reduced ? "instant" : "", pageHidden ? "tw-hidden" : ""].filter(Boolean).join(" ");

  useLayoutEffect(() => {
    // a desk that disappears must not leave a stale position behind
    for (const k of [...artRefs.current.keys()]) if (!onFloor.some((a) => a.id === k)) artRefs.current.delete(k);
  }, [onFloor]);

  return (
    <div className="screen" data-screen="teams" style={{ background: "#000" }}>
      <div className={"tw-bloom" + (open && !reduced ? " on" : "")} aria-hidden />
      {open && !closing && !reduced && <div className="tw-ripple" aria-hidden />}
      <div className={cls} ref={rootRef} data-state={status} data-tour={tour === null ? undefined : TOUR[tour]?.spot} onScroll={(e) => { e.currentTarget.scrollTop = 0; e.currentTarget.scrollLeft = 0; }}>
        <div className="tw-wall" />
        <div className="tw-base" />
        <div className="tw-floorbg" />
        <div className="tw-glow" />
        {sparks && (
          <div className="tw-sparks" aria-hidden>
            {SPARKS.map((sp, i) => (
              <i key={i} style={{ left: `${sp.x}%`, animationDelay: `${sp.d}ms`, width: sp.s, height: sp.s, ["--dx" as string]: `${sp.dx}px` }} />
            ))}
          </div>
        )}
        <div className="tw-plant l"><div className="tw-leaves"><i className="tw-leaf" /><i className="tw-leaf b" /></div><i className="tw-pot" /></div>
        <div className="tw-plant r"><i className="tw-ball" /><i className="tw-pot s" /></div>

        <div className="tw-top">
          <button className="tw-pill" onClick={leave} aria-label="Back to Home"><span className="chev">‹</span>Home</button>
          <h1 className="tw-title" style={{ margin: 0 }}>
            Teams
            {first && <span className="tw-newchip">NEW</span>}
          </h1>
          <button className="tw-pill tw-help" onClick={() => setTour(0)} aria-label="What is this? Take the tour">?</button>
          <button
            className="tw-pill chip"
            aria-label={summaries.length > 1 ? `Computer: ${computer?.label}. Tap for the next one.` : `Computer: ${computer?.label ?? ""}`}
            onClick={() => {
              if (summaries.length < 2) return;
              const i = summaries.findIndex((s) => s.computer.id === id);
              setPick(summaries[(i + 1) % summaries.length]!.computer.id);
            }}
          >
            <i className="tw-dotg" style={{ background: ready ? "#00ca50" : "#8e8ca0" }} />
            {computer?.label ?? "No computer"}
          </button>
        </div>

        {/* the coordinator */}
        <button className="tw-lead" ref={leadRef} disabled={status !== "team"} onClick={openChat} aria-label={`Talk to ${lead?.name ?? "the coordinator"}`} data-testid="team-lead">
          <span className="tw-float"><Buddy color={lookOf({ id: leadId, provider: "claude", isGod: true }).color} shape="50%" acc="sprout" size={72} mood={status === "team" ? mood : "idle"} /></span>
          <span className="tw-shadow" />
          <span className="tw-name">{lead?.name ?? "Dot"}</span>
        </button>
        <button className="tw-board" disabled={status !== "team"} onClick={openChat} aria-label="The plan. Open the chat.">
          <div className="tw-board-head">
            <span className="tw-board-title">{status === "team" ? boardTitle : "Not running"}</span>
            <span className="tw-board-prog">{boardProg}</span>
          </div>
          {status !== "team" || tasks.length === 0 ? (
            <div className="tw-board-empty">{status === "team" ? `Tell ${lead?.name ?? "Dot"} what you want done. The plan shows up here.` : "The plan shows up here once the team is running."}</div>
          ) : (
            <div className="tw-rows">
              {tasks.slice(0, BOARD_ROWS).map((t) => {
                const r = boardRow(t.status);
                const owner = t.assignee ? lookFor(t.assignee) : null;
                return (
                  <div key={t.id} className="tw-row" style={{ ["--i" as string]: tasks.indexOf(t) }}>
                    <span className={"g" + (r.pulse ? " tw-pulse-row" : "")} style={{ color: r.gColor }}>{r.glyph}</span>
                    <span className="t" style={{ color: r.tColor }}>{t.title}</span>
                    {owner && <i className="o" style={{ background: owner.color, borderRadius: owner.shape }} />}
                  </div>
                );
              })}
              {tasks.length > BOARD_ROWS && <div className="tw-more">+{tasks.length - BOARD_ROWS} more</div>}
            </div>
          )}
        </button>

        {/* the desks */}
        <div className="tw-floor">
          {status === "none" && <div className="tw-note"><strong>No computers yet</strong><p>Pair a computer to run a team of agents on it.</p></div>}
          {status === "old" && <div className="tw-note"><strong>This computer can't run a team yet.</strong><p>Update Brocoded on the computer, then come back.</p></div>}
          {status === "wait" && <div className="tw-wait" role="status">{ready && <Spinner size={14} />}<span>{ready ? "Looking for the team…" : "Waiting for the computer"}</span></div>}
          {status === "off" && (eng === "stopped" || eng === "failed") && (
            <div className="tw-note" data-testid="team-start-card">
              <strong>{eng === "failed" ? "The team stopped." : "Start your team"}</strong>
              <p>{eng === "failed" ? (office?.engineMessage ?? "It didn't start.") : "Agents on a team work on their own, without asking before each command. They run Claude Code on this computer, in the folders you point them at. To let them start, this also turns off Claude Code's own warning about running without prompts, for every Claude Code session on this computer."}</p>
              <button className="tw-cta" disabled={engineBusy} onClick={() => void runEngine("start")} data-testid="team-start">
                {engineBusy && <Spinner size={14} />}{eng === "failed" ? "Try again" : "Start the team"}
              </button>
            </div>
          )}
          {status === "off" && (eng === "starting" || eng === "running") && <div className="tw-wait" role="status"><Spinner size={14} /><span>Starting the team…</span></div>}
          {status === "off" && eng === "unavailable" && <div className="tw-note"><strong>This Brocoded doesn't include the team yet.</strong><p>Update Brocoded on the computer, then come back.</p></div>}
          {status === "off" && !eng && <div className="tw-note"><strong>The team isn't running on this computer.</strong><p>{office?.reason ?? "Start the team on the computer, then this fills in by itself."}</p></div>}
          {status === "team" && onFloor.length === 0 && <div className="tw-note"><strong>The room is empty.</strong><p>Tell {lead?.name ?? "the coordinator"} what you want done. It brings in agents as it needs them.</p><div className="tw-cta"><button className="tw-act" onClick={openChat}>Give {lead?.name ?? "the coordinator"} a goal</button></div></div>}
          <div className={"tw-grid" + (onFloor.length > 6 ? " many" : "")}>
            {onFloor.map((a, i) => {
              const d = deskFor(a);
              const look = lookOf(a);
              const c = deskColors(d.state);
              const [pb, pf] = PILL[d.state];
              const anim = d.state === "work" ? "tw-anim-type" : d.state === "wait" ? "tw-anim-hop" : d.state === "hand" ? "tw-anim-float" : "";
              return (
                <div key={a.id} className="tw-deskwrap">
                  <button className="tw-desk" style={{ transitionDelay: `${(120 + i * 90) * k}ms`, ["--i" as string]: i }} onClick={() => setSheet({ kind: "agent", id: a.id })} data-testid={`team-agent-${a.id}`} data-state={d.state} aria-label={`${a.name}. ${d.label}. ${d.task}`}>
                    <div className="tw-art" ref={(el) => { if (el) artRefs.current.set(a.id, el); }}>
                      <div className="tw-halo" style={{ background: c.halo }} />
                      <div className={"tw-char " + anim}><Buddy color={look.color} shape={look.shape} acc={look.acc} size={60} mood={MOOD[d.state]} /></div>
                      {d.state === "work" && <div className="tw-bubble-typing"><i /><i /><i /></div>}
                      {d.state === "wait" && <div className="tw-badge wait">!</div>}
                      {(d.state === "fail" || d.state === "stopped") && <div className="tw-badge fail">×</div>}
                      {d.state === "done" && <div className="tw-badge done">✓</div>}
                      {d.state === "hand" && <div className="tw-badge hand">→</div>}
                      <div className="tw-screen" style={{ background: c.screen }}>
                        {d.state === "work" && <div className="tw-code"><i /><i /></div>}
                        <b style={{ color: c.screenFg }}>{c.glyph}</b>
                      </div>
                      <div className="tw-stand" />
                      <div className="tw-top-desk" />
                      <div className="tw-front" />
                    </div>
                    <div className="tw-who"><span className="n">{a.name}</span><span className="a">{a.provider}</span></div>
                    <div className="tw-state" style={{ background: pb, color: pf }}>{d.label}</div>
                    <div className="tw-task">{d.task}</div>
                  </button>
                </div>
              );
            })}
          </div>
          <EarlierList agents={earlier} onOpen={(aid) => setSheet({ kind: "agent", id: aid })} />
          {status === "team" && eng === "running" && (
            <button
              className="tw-stop"
              disabled={engineBusy}
              onClick={() => {
                if (!stopArmed) return setStopArmed(true);
                setStopArmed(false);
                void runEngine("stop");
              }}
              data-testid="team-stop"
            >
              {stopArmed ? "Tap again to stop the team" : "Stop the team"}
            </button>
          )}
        </div>

        {/* a hand-over in flight */}
        {flight && (
          <>
            <svg className="tw-line" aria-hidden>
              <line x1={flight.x1} y1={flight.y1} x2={flight.x2} y2={flight.y2} />
              <circle cx={flight.x2} cy={flight.y2} r="38" />
            </svg>
            <div className="tw-token" style={{ left: (flight.at ? flight.x2 : flight.x1) - 17, top: (flight.at ? flight.y2 : flight.y1) - 12 }} aria-hidden><i /><i /></div>
          </>
        )}

        {status === "team" && bubble && !sheet && (
          <button className={"tw-say" + (bubble.attn ? " attn" : "")} onClick={() => (bubble.act === "agent" && bubble.agentId ? setSheet({ kind: "agent", id: bubble.agentId }) : openChat())} data-testid="team-say">
            <span>{bubble.text}</span>
            {bubble.label && <b>{bubble.label}</b>}
          </button>
        )}
        {status === "team" && (
          <button className="tw-dock" onClick={openChat} data-testid="team-dock">
            <Buddy color="oklch(0.8 0.12 295)" shape="50%" acc="sprout" size={34} mood="happy" />
            <span>Ask {lead?.name ?? "Dot"} anything…</span>
            <b aria-hidden>↑</b>
          </button>
        )}

        {tour !== null && !sheet && <TeamTour step={tour} onStep={setTour} onDone={endTour} />}

        <Sheet open={sheet?.kind === "agent" && !!sel} onClose={closeSheet} label={sel ? `${sel.name}` : "Agent"}>
          {sel && <AgentSheet key={sel.id} conn={conn} agent={sel} desk={deskFor(sel)} tasks={tasks} live={live[sel.id]} ready={ready} computerName={computer?.label ?? "this computer"} onClose={closeSheet} toast={toast} />}
        </Sheet>
        <Sheet open={sheet?.kind === "chat"} onClose={closeSheet} label={`Chat with ${lead?.name ?? "the coordinator"}`}>
          <ChatSheet lead={lead} workers={onFloor} mood={mood} entries={entries} typing={typing} plan={planCard} summary={summary} nameOf={nameOf} lookFor={lookFor} suggestions={SUGGESTIONS} onSend={send} onClose={closeSheet} onOpenHandover={(m) => setSheet({ kind: "hand", m })} prefill={prefill} onChangePlan={() => setPrefill({ text: "Change the plan: ", n: Date.now() })} />
        </Sheet>
        <Sheet open={sheet?.kind === "hand"} hand onClose={closeSheet} label="Hand-over">
          {sheet?.kind === "hand" && <HandoverSheet message={sheet.m} from={byId(sheet.m.from)} to={byId(sheet.m.to)} onClose={closeSheet} onOpen={(aid) => setSheet({ kind: "agent", id: aid })} />}
        </Sheet>
      </div>
    </div>
  );
}

function EarlierList({ agents, onOpen }: { agents: OfficeAgent[]; onOpen: (id: string) => void }) {
  const [shown, setShown] = useState(false);
  if (agents.length === 0) return null;
  return (
    <div className="tw-earlier">
      <button onClick={() => setShown((v) => !v)} aria-expanded={shown}>Earlier · {agents.length} <span aria-hidden>{shown ? "⌄" : "›"}</span></button>
      {shown && (
        <ul>
          {agents.map((a) => {
            const l = lookOf(a);
            return (
              <li key={a.id}>
                <button onClick={() => onOpen(a.id)}>
                  <Buddy color={l.color} shape={l.shape} acc={l.acc} size={28} mood="done" />
                  <span>{a.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
